import QRCode from "react-qr-code";
import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";
import { getAuthRedirectUrl, requestPasswordReset } from "../helpers/authHelpers";
import { AUTH_SESSION_OFFER_KIND } from "../helpers/authSessionOffer";
import {
  formatPasswordResetError,
  formatSignInError,
} from "../helpers/authUserMessages";
import { devLog } from "../helpers/devLog";
import { CPInput } from "./CPInput";
import { ViewportPortal } from "./shared/ViewportDialog";
import "./CPInput.css";

function isEmailNotConfirmedError(error) {
  if (!error) return false;
  if (error.code === "email_not_confirmed") return true;
  return (error.message || "").toLowerCase().includes("email not confirmed");
}

export function OnboardingScreen({
  onContinue,
  onSignupResult,
  onNeedsEmailConfirmation,
  initialEmail = "",
  initialShowEmailForm = false,
  loginSuccessMessage = "",
  onClearLoginSuccessMessage,
  showGuestOption = true,
  cachedSessionOffer = null,
  onContinueWithCachedSession,
}) {
  const [showQR, setShowQR] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(initialShowEmailForm);
  const [authView, setAuthView] = useState("signin");
  const [formError, setFormError] = useState(null);
  const [showUnverifiedLink, setShowUnverifiedLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authMode, setAuthMode] = useState(null);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
    if (initialShowEmailForm) {
      setShowEmailInput(true);
    }
  }, [initialEmail, initialShowEmailForm]);

  const handleSendResetLink = async () => {
    clearFormFeedback();

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError("Please enter your email address.");
      return;
    }

    setAuthMode("forgot");

    try {
      devLog("[AUTH DEBUG] OnboardingScreen: sending reset link");
      const { error } = await requestPasswordReset(trimmedEmail);

      if (error) {
        console.error("[AUTH DEBUG] resetPasswordForEmail error:", error);
        setFormError(formatPasswordResetError(error));
        return;
      }

      setAuthView("forgotSent");
    } finally {
      setAuthMode(null);
    }
  };

  const handleBackToSignInFromForgot = () => {
    clearFormFeedback();
    setAuthView("signin");
    onClearLoginSuccessMessage?.();
  };

  const handleOpenForgotPassword = () => {
    clearFormFeedback();
    onClearLoginSuccessMessage?.();
    setAuthView("forgot");
  };

  const clearFormFeedback = () => {
    setFormError(null);
    setShowUnverifiedLink(false);
  };

  const handleGuest = () => {
    onContinue({
      email: null,
      isGuest: true,
    });
  };

  const handleEmailSignIn = async () => {
    clearFormFeedback();

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setFormError("Please enter your email and password.");
      return;
    }

    setAuthMode("signin");

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (error) {
        if (isEmailNotConfirmedError(error)) {
          setFormError("Your email isn't verified yet.");
          setShowUnverifiedLink(true);
        } else {
          setFormError(formatSignInError(error));
        }
        return;
      }
    } finally {
      setAuthMode(null);
    }
  };

  const handleCreateAccount = async () => {
    clearFormFeedback();

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setFormError("Please enter your email and password.");
      return;
    }

    if (password.length < 6) {
      setFormError("Password must be at least 6 characters.");
      return;
    }

    setAuthMode("signup");

    try {
      const { data, error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          emailRedirectTo: getAuthRedirectUrl(),
        },
      });

      if (error) {
        setFormError(error.message);
        return;
      }

      onSignupResult({
        email: trimmedEmail,
        session: data.session,
        user: data.user,
      });
    } finally {
      setAuthMode(null);
    }
  };

  const handleGitHubLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "github",
      options: {
        redirectTo: getAuthRedirectUrl(),
      },
    });

    if (error) {
      console.error("GitHub login error:", error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getAuthRedirectUrl(),
      },
    });

    if (error) {
      console.error("Google sign in error:", error.message);
    }
  };

  const isAuthBusy = authMode !== null;
  const showCachedSessionOffer =
    cachedSessionOffer?.kind === AUTH_SESSION_OFFER_KIND.CONTINUE_CACHED;
  const showExpiredSessionNotice =
    cachedSessionOffer?.kind === AUTH_SESSION_OFFER_KIND.EXPIRED;

  const handleContinueWithCachedSession = () => {
    clearFormFeedback();
    onContinueWithCachedSession?.();
  };

  return (
    <div className="screen onboarding-screen">
      <div className="onboarding-content">
        <div className="onboarding-text">
          <h1 className="onboarding-title">CrewPilot</h1>

          <p className="onboarding-subtitle">Powered by Nexora Systems</p>
        </div>

        <div className="onboarding-actions">
          {showCachedSessionOffer && (
            <div
              className="onboarding-cached-session-offer"
              style={{
                width: "100%",
                marginBottom: "16px",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid rgba(212,175,55,0.28)",
                background: "rgba(212,175,55,0.08)",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px",
                  color: "#f5f5f5",
                  fontWeight: 600,
                  fontSize: "1rem",
                }}
              >
                {cachedSessionOffer.headline}
              </p>
              <p style={{ margin: "0 0 12px", color: "rgba(255,255,255,0.78)" }}>
                {cachedSessionOffer.detail}
              </p>
              <p style={{ margin: "0 0 12px", color: "#c9a84c" }}>
                Signed in as {cachedSessionOffer.displayName}
              </p>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                onClick={handleContinueWithCachedSession}
                disabled={isAuthBusy}
              >
                Continue with saved session
              </button>
            </div>
          )}

          {showExpiredSessionNotice && (
            <div
              className="onboarding-session-expired-notice"
              style={{
                width: "100%",
                marginBottom: "16px",
                padding: "14px 16px",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
              }}
            >
              <p style={{ margin: "0 0 6px", color: "#f5f5f5", fontWeight: 600 }}>
                {cachedSessionOffer.headline}
              </p>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.78)" }}>
                {cachedSessionOffer.detail}
              </p>
            </div>
          )}

          {!showEmailInput ? (
            <>
              <button
                className="nx-btn nx-btn-gold"
                onClick={() => setShowEmailInput(true)}
              >
                Sign In with Email
              </button>

              <button
                className="nx-btn"
                onClick={handleGoogleSignIn}
                style={{
                  background: "#ffffff",
                  color: "#111111",
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
              >
                Continue with Google
              </button>

              <button
                className="nx-btn nx-btn-github"
                onClick={handleGitHubLogin}
              >
                Continue with GitHub
              </button>

              {showGuestOption && (
                <button className="nx-btn nx-btn-ghost" onClick={handleGuest}>
                  Continue as Guest
                </button>
              )}
            </>
          ) : authView === "forgotSent" ? (
            <div className="ci-success-content" style={{ width: "100%" }}>
              <h1 className="ci-success-title" style={{ fontSize: "1.5rem" }}>
                Check your email
              </h1>
              <p className="ci-success-sub">
                If an account exists for this address, we sent password reset
                instructions to:
              </p>
              {email && (
                <p
                  style={{
                    color: "#c9a84c",
                    fontWeight: 600,
                    marginBottom: "16px",
                  }}
                >
                  {email.trim()}
                </p>
              )}
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                onClick={handleBackToSignInFromForgot}
              >
                Back to Sign In
              </button>
            </div>
          ) : authView === "forgot" ? (
            <>
              <CPInput
                label="Work Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(next) => {
                  setEmail(next);
                  clearFormFeedback();
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSendResetLink()}
                autoFocus
                autoComplete="email"
                disabled={isAuthBusy}
              />

              {formError && <p className="nx-form-error">{formError}</p>}

              <button
                type="button"
                className="nx-btn nx-btn-gold"
                onClick={handleSendResetLink}
                disabled={isAuthBusy}
              >
                {authMode === "forgot" ? "Sending…" : "Send Reset Link"}
              </button>

              <button
                type="button"
                className="nx-btn nx-btn-ghost"
                onClick={handleBackToSignInFromForgot}
                disabled={isAuthBusy}
              >
                Back to Sign In
              </button>
            </>
          ) : (
            <>
              <CPInput
                label="Work Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(next) => {
                  setEmail(next);
                  clearFormFeedback();
                }}
                onKeyDown={(e) => e.key === "Enter" && handleEmailSignIn()}
                autoFocus
                autoComplete="email"
                disabled={isAuthBusy}
              />
              {/* Password + show/hide toggle: specialized composite using CP* chrome */}
              <div className="cp-input-group">
                <label className="cp-input-label">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    className="cp-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearFormFeedback();
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailSignIn()}
                    autoComplete="current-password"
                    disabled={isAuthBusy}
                    style={{ paddingRight: "70px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      color: "#d4af37",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {loginSuccessMessage && (
                <p className="nx-form-success">{loginSuccessMessage}</p>
              )}

              {formError && (
                <div>
                  <p className="nx-form-error">{formError}</p>
                  {showUnverifiedLink && (
                    <button
                      type="button"
                      className="nx-form-error-link"
                      onClick={() => onNeedsEmailConfirmation(email.trim())}
                    >
                      Resend confirmation email
                    </button>
                  )}
                </div>
              )}

              <button
                type="button"
                className="nx-form-error-link"
                style={{ alignSelf: "flex-start", marginBottom: "8px" }}
                onClick={handleOpenForgotPassword}
                disabled={isAuthBusy}
              >
                Forgot password?
              </button>

              <button
                className="nx-btn nx-btn-gold"
                onClick={handleEmailSignIn}
                disabled={isAuthBusy}
              >
                {authMode === "signin" ? "Signing in…" : "Sign In"}
              </button>
              <button
                className="nx-btn"
                onClick={handleCreateAccount}
                disabled={isAuthBusy}
              >
                {authMode === "signup" ? "Creating account…" : "Create Account"}
              </button>

              {showGuestOption && (
                <button
                  className="nx-btn nx-btn-ghost"
                  onClick={handleGuest}
                  disabled={isAuthBusy}
                >
                  Skip — Continue as Guest
                </button>
              )}
            </>
          )}

          <div className="business-card-section">
            <button
              className="business-link-btn"
              onClick={() => setShowQR(true)}
            >
              www.NexoraSystems.ca
            </button>
          </div>
        </div>
      </div>

      {showQR && (
        <ViewportPortal>
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.82)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "24px",
          }}
          onClick={() => setShowQR(false)}
        >
          <div
            style={{
              width: "min(92vw, 420px)",
              background: "#0b0b0f",
              border: "1px solid rgba(212,175,55,0.22)",
              borderRadius: "24px",
              padding: "32px 24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "24px",
              boxShadow: "0 0 40px rgba(0,0,0,0.45)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <QRCode value="https://www.NexoraSystems.ca" size={220} />
          </div>
        </div>
        </ViewportPortal>
      )}
    </div>
  );
}
